import React from "react";
import { Box, Typography, Grid, Stack } from "@mui/material";

/**
 * Helper component for rendering each numbered feature item.
 * It handles the layout of the purple circle and the text box.
 * @param {object} props
 * @param {string} props.number - The number to display in the circle.
 * @param {React.ReactNode} props.text - The text content for the box.
 * @param {'left' | 'right'} [props.circlePosition='left'] - Determines if the circle is on the left or right of the text.
 */
const FeatureItem = ({ number, text, circlePosition = "left" }) => {
  // Styles are defined as objects for the 'sx' prop (MUI's inline styling solution).

  // Base style for the container of a single feature item.
  // 'inline-flex' allows it to be aligned by its parent's alignment properties.
  const itemStyle = {
    display: "inline-flex",
    alignItems: "center",
    gap: 2, // 16px gap
    flexDirection: circlePosition === "left" ? "row" : "row-reverse",
    maxWidth: "100%",
  };

  // Style for the purple circle.
  const circleStyle = {
    backgroundColor: "#6a58c6", // Purple color from the image
    color: "white",
    width: { xs: 70, md: 80 }, // Responsive width
    height: { xs: 70, md: 80 }, // Responsive height
    borderRadius: "50%",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0, // Prevents the circle from shrinking on smaller viewports
  };

  // Style for the number inside the circle.
  const numberStyle = {
    fontSize: { xs: "2.2rem", md: "2.5rem" },
    fontWeight: "bold",
    fontFamily: "sans-serif",
  };

  // Style for the text box.
  const textBoxStyle = {
    border: "1px solid #333",
    borderRadius: "12px",
    padding: "12px 16px",
    backgroundColor: "white",
    textAlign: "left", // Text inside the box is always left-aligned per the image
  };

  const textStyle = {
    fontSize: "1rem",
    fontFamily: "sans-serif",
    lineHeight: 1.4,
  };

  return (
    <Box sx={itemStyle}>
      <Box sx={circleStyle}>
        <Typography sx={numberStyle}>{number}</Typography>
      </Box>
      <Box sx={textBoxStyle}>
        <Typography sx={textStyle}>{text}</Typography>
      </Box>
    </Box>
  );
};

/**
 * The main component that replicates the provided "How we Solve it 4 You?" image layout.
 * It's designed to be responsive and uses Material-UI for the grid system.
 */
const Solve = () => {
  // Main container styling.
  const containerStyle = {
    padding: { xs: 3, md: 5 }, // Responsive padding
    backgroundColor: "#FFFFFF",
    fontFamily: "sans-serif",
    overflow: "hidden", // Prevents horizontal scroll
  };

  // Title styling.
  const titleStyle = {
    textAlign: "center",
    fontWeight: "bold",
    marginBottom: { xs: 4, md: 2 },
    fontSize: { xs: "1.8rem", sm: "2.2rem", md: "2.5rem" }, // Responsive font size
  };

  // Styling for the large central '4'.
  const bigFourStyle = {
    fontSize: { xs: "12rem", md: "20rem" }, // Responsive font size
    fontWeight: 900,
    color: "black",
    lineHeight: 1,
    textAlign: "center",
    fontFamily: '"Arial Black", "Helvetica Bold", sans-serif',
  };

  return (
    <Box sx={containerStyle}>
      <Typography variant="h2" component="h2" sx={titleStyle}>
        How we Solve it 4 You?
      </Typography>

      <Grid
        container
        alignItems="center"
        justifyContent="center"
        spacing={{ xs: 4, md: 1 }}
      >
        {/* Left Column: On desktop, content is aligned to the right to cluster around the center. */}
        <Grid item xs={12} md={5}>
          <Stack
            spacing={{ xs: 4, md: 6 }}
            alignItems={{ xs: "center", md: "flex-end" }}
          >
            <FeatureItem
              number="1"
              text={
                <>
                  Mock Assessment for Human
                  <br />
                  Centric Interview Preparation
                </>
              }
              circlePosition="left"
            />
            <FeatureItem
              number="2"
              text={
                <>
                  Workshop & Doubt Session
                  <br />
                  for students to understand
                </>
              }
              circlePosition="right"
            />
          </Stack>
        </Grid>

        {/* Center Column: The big '4'. This will stack between the two groups on mobile. */}
        <Grid item xs={12} md={2}>
          <Typography sx={bigFourStyle}>4</Typography>
        </Grid>

        {/* Right Column: On desktop, content is aligned to the left. */}
        <Grid item xs={12} md={5}>
          <Stack
            spacing={{ xs: 4, md: 6 }}
            alignItems={{ xs: "center", md: "center" }}
          >
            <FeatureItem
              number="3"
              text={
                <>
                  New Talent Acquisition Market
                  <br />
                  Ready Placement Assessment
                </>
              }
              circlePosition="right"
            />
            <FeatureItem
              number="4"
              text={
                <>
                  Individual & Consolidated
                  <br />
                  Report for Improvement goals
                </>
              }
              circlePosition="left"
            />
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Solve;
